import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from '@angular/core';
import {
  InspectionEntryDetailDto,
  InspectionEntryItemDto,
  InspectionEntryService,
  RepairTaskDetailDto,
  RepairTaskService,
} from '@artemis-software/wr-api';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { CurrentUserService } from '@services/current-user.service';
import { RepairTaskDispatchService } from '@services/repair-task-dispatch.service';
import {
  openRepairTaskEditDialogForOutcome,
} from '@components/dialog/repair-task-edit-dialog/repair-task-edit-dialog.component';

export type InspectionEntryRepairTaskPatch = Pick<InspectionEntryItemDto, 'repairTaskId' | 'repairTaskStatus'>;

@Component({
  standalone: false,
  selector: 'wr-inspection-entry-statistic-dialog',
  templateUrl: './inspection-entry-statistic-dialog.component.html',
  styleUrls: ['./inspection-entry-statistic-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectionEntryStatisticDialogComponent implements OnInit {

  private readonly http = inject(HttpClient);
  private readonly inspectionEntryService = inject(InspectionEntryService);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(NbDialogRef);
  private readonly dialogService = inject(NbDialogService);
  private readonly repairTaskService = inject(RepairTaskService);
  private readonly repairTaskDispatchService = inject(RepairTaskDispatchService);
  private readonly currentUserService = inject(CurrentUserService);

  @Input()
  item!: InspectionEntryItemDto;

  @Input()
  showRepairTask = false;

  detail$!: Observable<InspectionEntryDetailDto>;
  readonly lightboxUrl = signal<string | null>(null);
  readonly exporting = signal(false);
  readonly repairTask = signal<RepairTaskDetailDto | null>(null);
  readonly repairTaskBusy = signal(false);
  readonly canCreateRepairTask = this.currentUserService.hasPrivilege('canEditRepairTasks');

  ngOnInit(): void {
    this.detail$ = this.inspectionEntryService.findById(this.item.id);
    if (this.showRepairTask && this.item.repairTaskId) {
      void this.loadRepairTask(this.item.repairTaskId);
    }
  }

  async createRepairTask(detail: InspectionEntryDetailDto): Promise<void> {
    const description = [detail.predefinedAssessment?.text, detail.note]
      .filter(text => !!text?.trim())
      .join('\n\n');
    await this.runRepairTaskAction(() => this.repairTaskDispatchService.createAndSend({
      buildingId: this.item.buildingId,
      inspectionEntryId: this.item.id,
      title: `${detail.checkpoint.number} ${detail.checkpoint.name}`.trim(),
      description,
    }));
  }

  async openRepairTask(repairTask: RepairTaskDetailDto): Promise<void> {
    const outcome = await openRepairTaskEditDialogForOutcome(this.dialogService, repairTask.id);
    if (!outcome) {
      return;
    }
    if (!outcome.task) {
      this.closeAfterRepairTaskChange(null);
      return;
    }
    await this.runRepairTaskAction(() => firstValueFrom(this.repairTaskService.findById(repairTask.id)));
  }

  private async runRepairTaskAction(action: () => Promise<RepairTaskDetailDto | undefined>): Promise<void> {
    this.repairTaskBusy.set(true);
    try {
      const result = await action();
      if (result) {
        this.closeAfterRepairTaskChange(result);
      }
    } finally {
      this.repairTaskBusy.set(false);
    }
  }

  private async loadRepairTask(id: string): Promise<void> {
    this.repairTask.set(await firstValueFrom(this.repairTaskService.findById(id)));
  }

  navigateToInspection(): void {
    this.dialogRef.close();
    void this.router.navigate(['/inspection', this.item.inspectionId]);
  }

  private closeAfterRepairTaskChange(task: RepairTaskDetailDto | null): void {
    const patch: InspectionEntryRepairTaskPatch = {
      repairTaskId: task?.id,
      repairTaskStatus: task?.status,
    };
    this.dialogRef.close(patch);
  }

  openLightbox(url: string | undefined): void {
    if (url) this.lightboxUrl.set(url);
  }

  closeLightbox(): void {
    this.lightboxUrl.set(null);
  }

  async exportPdf(): Promise<void> {
    if (this.exporting()) {
      return;
    }
    this.exporting.set(true);
    try {
      const blob = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/api/inspectionEntry/${this.item.id}/exportPdf`, {
          responseType: 'blob',
          headers: { 'Accept': 'application/pdf' },
        }),
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pruefpunkteintrag.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      this.exporting.set(false);
    }
  }
}
